import {
  CHANNEL_NAMES,
  CHANNEL_SYNC_ADVICE,
  cancelReasonText,
  cancelledLinesText,
  channelSyncWarningText,
  doNotShipText,
  repackText,
  scanSpellings,
  type CancelledParcel,
  type ChannelSyncWarning,
  type RepackParcel,
} from '@wms/domain';
import { useEffect, useRef, useState } from 'react';
import { ACTION_TIMEOUT_MS, ApiError, api, apiBlob, errorMessage, type FloorAction } from '../api';
import { printPdf, printZpl } from '../printing';
import { feedback } from '../feedback';
import { carrierLabel, fmt, statusLabel } from '../format';
import { useSession } from '../session';
import type { OrderSummary } from '../types';
import { ChannelBadge, Empty, ErrorBanner, ScanField, SellerChip, Spinner, useFloorAction, useLoad } from '../ui';
import { Icon } from '../ui/icons';
import { trackingOf } from '../tracking';
import { CarrierState, TrackingNumber } from '../ui/tracking';
import { NextWorkStep } from '../ui/NextStep';
import { labelNow, labelOf, labelVoid, pileOf, voidLabelText } from '../dock';
import { relabelChip, relabelStopText } from '../urgent';
import { useUrgent } from './Urgent';

/** One answer of the dock. */
interface DockAnswer {
  /** a new answer is a new line: its flash plays again */
  readonly id: number;
  readonly ok: boolean;
  readonly text: string;
  /** handed over: can be taken back from its line */
  readonly shipped?: OrderSummary;
  /** KARGOYA VERMEYİN: the label of a cancelled order */
  readonly stop?: boolean;
}

let answers = 0;

/** Kargo teslimi: the courier's scanner or ours reads each parcel label at the dock. */
export function Dispatch() {
  const { warehouseId, isSupervisor } = useSession();
  const amp = warehouseId ? `&warehouseId=${warehouseId}` : '';
  const packed = useLoad(() => api<OrderSummary[]>('GET', `/api/orders?status=packed&sort=oldest&limit=500${amp}`), [amp], 10_000);
  // packed, then cancelled: these parcels must not leave
  const cancelled = useLoad(
    () => api<CancelledParcel[]>('GET', `/api/wms/dispatch/cancelled${warehouseId ? `?warehouseId=${warehouseId}` : ''}`),
    [warehouseId],
    10_000,
  );
  // packed, then a line of it cancelled: opened again at a desk before they leave
  const repack = useLoad(
    () => api<RepackParcel[]>('GET', `/api/wms/dispatch/repack${warehouseId ? `?warehouseId=${warehouseId}` : ''}`),
    [warehouseId],
    10_000,
  );
  // the dock's latest answers; a handover can be taken back from its line
  const [log, setLog] = useState<DockAnswer[]>([]);
  // the newest answer in sight: on a phone the KARGOYA VERMEYİN box above pushes it below the fold
  const newest = useRef<HTMLDivElement>(null);
  useEffect(() => {
    newest.current?.scrollIntoView({ block: 'nearest' });
  }, [log[0]?.id]);
  // the buttons (teslim et, geri al): a lost answer is sent again with its key
  const dock = useFloorAction();
  // parcels handed over from this screen, with the boxes read: a many-box parcel's every box carries the same label
  const handed = useRef(new Map<string, { order: OrderSummary; boxes: number }>());
  // parcels whose carrier changed after their label printed (acil iş): they leave on their new label's scan
  const urgent = useUrgent();
  const relabel = new Map((urgent?.actions ?? []).filter((a) => a.kind === 'relabel').map((a) => [a.orderId as string, a]));

  const note = (ok: boolean, text: string, shipped?: OrderSummary) => {
    feedback(ok ? 'ok' : 'error');
    setLog((all) => [{ id: ++answers, ok, text, ...(shipped ? { shipped } : {}) }, ...all].slice(0, 8));
  };

  // the loudest answer the dock gives: a cancelled order (doNotShipText), a parcel to be packed again (repackText)
  const stop = (orderNumber: string, text: string) => {
    feedback('error');
    setLog((all) => [{ id: ++answers, ok: false, stop: true, text: `${orderNumber}: ${text}` }, ...all].slice(0, 8));
    void packed.reload();
    void cancelled.reload();
    void repack.reload();
  };

  // code: the label the dock read (none: "Teslim et" without a reader). A parcel whose carrier changed after
  // its label printed leaves only on its new label's scan, which closes its acil iş
  const ship = async (order: OrderSummary, action?: FloorAction, code?: string) => {
    try {
      await api('POST', `/api/wms/orders/${order.id}/ship`, code ? { code } : undefined, { action });
      handed.current.set(order.id, { order, boxes: 1 });
      // a parcel of many boxes leaves whole: all of them, each with its label, go to the courier
      const boxes = (order.parcelCount ?? 1) > 1 ? ` · ${order.parcelCount} koli: hepsini kuryeye verin` : '';
      // its marketplace is not read: a cancel there would not have shown; the way back is on this line
      const unread = order.channelSync ? ` · Dikkat: ${channelSyncWarningText(order.channelSync)}, iptal edilmiş olabilir` : '';
      note(true, `${order.orderNumber} kargoya verildi${order.trackingNumber ? ` · ${order.trackingNumber}` : ''}${boxes}${unread}`, order);
      void packed.reload();
      // its new label's scan closed the acil iş: the red bar goes at once
      if (relabel.has(order.id)) void urgent?.reload();
    } catch (e) {
      // cancelled while it waited here (the list is a few seconds old): KARGOYA VERMEYİN says it all,
      // no "Bağlantı koptu — … SAYILMADI" next to it when this was a scan sent again
      if (e instanceof ApiError && e.code === 'ORDER_CANCELLED') {
        if (action) action.answered = true;
        return stop(order.orderNumber, doNotShipText((e.details as { putBack?: boolean } | undefined)?.putBack === true));
      }
      // a line of it was cancelled meanwhile: back to a desk, opened and packed again
      if (e instanceof ApiError && e.code === 'ORDER_REPACK') {
        if (action) action.answered = true;
        return stop(order.orderNumber, repackText((e.details as { cancelledLines?: RepackParcel['cancelledLines'] } | undefined)?.cancelledLines ?? []));
      }
      // its carrier changed after the label printed (acil iş): the old label read, or no label at all
      if (e instanceof ApiError && (e.code === 'RELABEL_OPEN' || e.code === 'LABEL_SUPERSEDED')) {
        if (action) action.answered = true;
        return stop(order.orderNumber, relabelStopText(e.code, (e.details as { trackingNumber?: string | null } | undefined)?.trackingNumber ?? order.trackingNumber));
      }
      note(false, `${order.orderNumber}: ${errorMessage(e)}`);
    }
  };

  // geri al: the courier did not take it after all; the parcel waits for the next one
  const [undoing, setUndoing] = useState<string | null>(null);
  const unship = async (order: OrderSummary, action?: FloorAction) => {
    setUndoing(order.id);
    try {
      const { channelTold } = await api<{ channelTold: boolean }>('POST', `/api/wms/orders/${order.id}/unship`, undefined, { action });
      setLog((all) => all.filter((entry) => entry.shipped?.id !== order.id));
      handed.current.delete(order.id);
      note(true, `${order.orderNumber}: teslim geri alındı, paket kargoya hazır bekliyor${channelTold ? ' (pazaryerine kargoya verildi bilgisi gitmişti)' : ''}`);
      void packed.reload();
    } catch (e) {
      note(false, `${order.orderNumber}: ${errorMessage(e)}`);
    } finally {
      setUndoing(null);
    }
  };

  const print = async (order: OrderSummary) => {
    try {
      const blob = await apiBlob(`/api/orders/${order.id}/documents/shipping_label`);
      if (blob.type.startsWith('text/')) await printZpl(await blob.text(), 'Kargo etiketi');
      else await printPdf(blob);
    } catch (e) {
      note(false, `${order.orderNumber}: ${errorMessage(e)}`);
    }
  };

  const onScan = async (scanned: string, action: FloorAction) => {
    // the code as read; failing that, as a reader on another keyboard layout meant it ("402*123" for 402-123)
    const spellings = scanSpellings(scanned);
    const listed = (s: string) =>
      Boolean(
        cancelled.data?.some((p) => labelOf(s, p)) ||
          repack.data?.some((p) => labelOf(s, p)) ||
          [...handed.current.values()].some((h) => labelOf(s, h.order)) ||
          packed.data?.some((o) => labelOf(s, o)),
      );
    let code = spellings.find(listed) ?? scanned;
    const dead = cancelled.data?.find((p) => labelOf(code, p));
    if (dead) return stop(dead.orderNumber, doNotShipText(true));
    const reopened = repack.data?.find((p) => labelOf(code, p));
    if (reopened) return stop(reopened.orderNumber, repackText(reopened.cancelledLines));
    // a label printed before the parcel's carrier changed: never to the courier, and no box counted. On a box of
    // a parcel handed over on its new label (the new label has a page per box), the new one goes on this box too
    const voided = [...handed.current.values()].find((h) => labelVoid(code, h.order));
    if (voided) return stop(voided.order.orderNumber, voidLabelText(voided.order, true));
    const waiting = packed.data?.find((o) => labelVoid(code, o));
    if (waiting) return stop(waiting.orderNumber, voidLabelText(waiting));
    // the next box of a parcel just handed over (the list may still show it): counted, not shipped again
    const again = [...handed.current.values()].find((h) => labelNow(code, h.order));
    if (again) {
      const total = again.order.parcelCount ?? 1;
      again.boxes += 1;
      if (again.boxes <= total) return note(true, `${again.order.orderNumber} · ${again.boxes}. koli okutuldu (${total} koli)`);
      return note(false, total > 1 ? `${again.order.orderNumber}: ${total} kolili gönderi, bu ${again.boxes}. okutma. Fazla koli var mı?` : `${again.order.orderNumber}: az önce kargoya verildi`);
    }
    const local = packed.data?.find((o) => o.trackingNumber === code || o.orderNumber.toUpperCase() === code);
    if (local) return ship(local, action, code);
    try {
      // a look-up changes nothing: no key, but no endless wait either
      const found = await api<OrderSummary[]>('GET', `/api/orders?search=${encodeURIComponent(code)}&limit=5${amp}`, undefined, { timeoutMs: ACTION_TIMEOUT_MS });
      // the search finds a reader's other spelling too: the one that names a parcel
      code = spellings.find((s) => found.some((o) => labelOf(s, o))) ?? code;
      // no label of a cancelled order goes with the courier, void or not; the dock's refusal
      // says whether its units still wait in Rafa iade (cancelled since the list was loaded)
      const gone = found.find((o) => o.status === 'cancelled' && labelOf(code, o));
      if (gone) return ship(gone, action, code);
      // a label printed before the marketplace moved the parcel to another carrier: never to the courier (a box
      // of a parcel handed over on another device: the new label goes on it too)
      const old = found.find((o) => labelVoid(code, o));
      if (old) return stop(old.orderNumber, voidLabelText(old, old.status === 'shipped'));
      const order = found.find((o) => o.trackingNumber === code || o.orderNumber.toUpperCase() === code);
      if (!order) return note(false, `${code}: kayıtlı paket bulunamadı`);
      // handed over already: said plainly, with the way back if the courier did not take it
      // (none when the carrier itself registered the parcel, or the marketplace shows it with its carrier: it has it)
      if (order.status === 'shipped' && order.carrierTracking?.trackingNumber) {
        return note(false, `${order.orderNumber}: zaten kargoya verildi, ${carrierLabel(order.carrierTracking.carrierCode)} paketi kaydetti · ${trackingOf(order).number}`);
      }
      if (order.status === 'shipped' && order.channelShippedAt) {
        return note(false, `${order.orderNumber}: zaten kargoya verildi, ${CHANNEL_NAMES[order.channel] ?? order.channel} paketi kargoda gösteriyor`);
      }
      // another box of a many-box parcel handed over elsewhere: no "Geri al" for one box of it
      if (order.status === 'shipped' && (order.parcelCount ?? 1) > 1) {
        return note(true, `${order.orderNumber}: ${order.parcelCount} kolili gönderi kargoya verildi; bu koli de onun, kuryeye verin`);
      }
      if (order.status === 'shipped') return note(false, `${order.orderNumber}: zaten kargoya verildi. Kurye almadıysa “Geri al”.`, order);
      // opened again for a cancelled line since the list was loaded: the dock's refusal names the line
      if (order.repackSince) return ship(order, action, code);
      if (order.status !== 'packed') return note(false, `${order.orderNumber}: ${statusLabel(order.status)}, teslim edilemez`);
      await ship(order, action, code);
    } catch (e) {
      note(false, errorMessage(e));
    }
  };

  // one pile per carrier and contract: the courier takes the parcels of the contract he comes for
  const byCarrier = new Map<string, OrderSummary[]>();
  for (const o of packed.data ?? []) {
    const key = pileOf(o);
    byCarrier.set(key, [...(byCarrier.get(key) ?? []), o]);
  }

  // parcels of marketplace accounts not read: a cancel made there is not seen, per account
  const unread = new Map<string, { warning: ChannelSyncWarning; parcels: number }>();
  for (const o of packed.data ?? []) {
    if (!o.channelSync) continue;
    const key = `${o.channelSync.channel}:${o.channelSync.label}`;
    unread.set(key, { warning: o.channelSync, parcels: (unread.get(key)?.parcels ?? 0) + 1 });
  }

  return (
    <>
      {cancelled.data && cancelled.data.length > 0 && <CancelledParcels parcels={cancelled.data} />}
      {repack.data && repack.data.length > 0 && <RepackParcels parcels={repack.data} />}
      {unread.size > 0 && (
        <div className="banner warn" role="status">
          <Icon name="alert" />
          <div style={{ flex: 1, minWidth: 0 }}>
            {[...unread.values()].map(({ warning, parcels }) => (
              <div key={`${warning.channel}:${warning.label}`} className="big">
                {channelSyncWarningText(warning)} · {parcels} paket
              </div>
            ))}
            {CHANNEL_SYNC_ADVICE}
          </div>
        </div>
      )}
      <div className="split list-wide">
        <div className="stack">
          <section className="card">
            <div className="card-b stack">
              <ScanField placeholder="Kargo etiketini okutun" onScan={onScan} suggest={{ kinds: ['order'], orderStatuses: ['packed'] }} hint="Takip numarası veya sipariş numarası. Her paket okutulduğunda kargoya verildi olarak işaretlenir." />
              {dock.banner}
              {log.map((entry, i) =>
                entry.stop ? (
                  <div key={entry.id} ref={i === 0 ? newest : undefined} className={`dns-alert${i === 0 ? '' : ' past'}`} role={i === 0 ? 'alert' : undefined}>
                    <Icon name="alert" size={i === 0 ? 36 : 22} />
                    <div>
                      <b>KARGOYA VERMEYİN</b>
                      <span>{entry.text}</span>
                    </div>
                  </div>
                ) : (
                  <div key={entry.id} ref={i === 0 ? newest : undefined} className={`banner ${entry.ok ? 'ok' : 'bad'}`} style={{ padding: '8px 12px', opacity: i === 0 ? 1 : 0.7, alignItems: 'center' }}>
                    <Icon name={entry.ok ? 'check' : 'alert'} />
                    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{entry.text}</span>
                    {entry.shipped && (
                      <button
                        className="btn sm"
                        disabled={undoing !== null || dock.waiting}
                        onClick={() => void dock.run('Geri al', (action) => unship(entry.shipped!, action))}
                        title="Kurye bu paketi almadıysa teslimi geri alır"
                      >
                        <Icon name="undo" size={16} /> Geri al
                      </button>
                    )}
                  </div>
                ),
              )}
            </div>
          </section>
          {packed.data?.length === 0 && (
            <NextWorkStep from="/sevkiyat" tone="ok" title="Kargoya verilecek paket yok" steps={['Paketlenen siparişler burada kargo firmasına göre listelenir.']} />
          )}
        </div>
        <section className="card">
          <div className="card-h">
            <Icon name="truck" />
            <h2>Kargoya hazır</h2>
            <span className="sub">{packed.data?.length ?? 0} paket</span>
          </div>
          {packed.error && <ErrorBanner message={packed.error} onRetry={() => void packed.reload()} />}
          {!packed.data ? (
            <Spinner />
          ) : packed.data.length === 0 ? (
            <Empty title="Bekleyen paket yok" />
          ) : (
            [...byCarrier.entries()].map(([carrier, orders]) => (
              <div key={carrier}>
                <div className="card-h" style={{ background: 'var(--surface-2)' }}>
                  <b>{carrier}</b>
                  <span className="sub">{orders.length} paket</span>
                </div>
                <div className="table-wrap">
                  <table className="t stack-sm">
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.id}>
                          <td>
                            <TrackingNumber order={o} />
                            <CarrierState tracking={o.carrierTracking} />
                          </td>
                          <td>
                            <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} short />
                          </td>
                          <td>
                            <SellerChip code={o.tenantCode} />
                            {relabel.has(o.id) && (
                              <span className="chip bad nowrap" style={{ marginLeft: 4 }} title="Etiketi basıldıktan sonra kargo değişti: yeni etiketi eskisinin üstüne yapıştırıp okutun; eski etiket reddedilir">
                                {relabelChip(relabel.get(o.id)!.details)}
                              </span>
                            )}
                            {(o.parcelCount ?? 1) > 1 && <span className="chip info nowrap" style={{ marginLeft: 4 }}>{o.parcelCount} koli</span>}
                            {o.channelSync && (
                              <span className="chip warn nowrap" style={{ marginLeft: 4 }} title={`${channelSyncWarningText(o.channelSync)}. ${CHANNEL_SYNC_ADVICE}`}>
                                Pazaryeri okunmuyor
                              </span>
                            )}
                          </td>
                          <td className="muted">{fmt.age(o.orderedAt)}</td>
                          <td className="num">
                            <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              <button className="btn sm ghost" onClick={() => void print(o)} title="Kargo etiketini bu bilgisayarın yazıcısından yeniden basar">
                                <Icon name="printer" size={16} /> Etiket
                              </button>
                              {isSupervisor && (
                                <button
                                  className="btn sm"
                                  disabled={dock.waiting}
                                  onClick={() => void dock.run('Teslim et', (action) => ship(o, action))}
                                  title="Etiketi okutmadan kargoya verildi olarak işaretler"
                                >
                                  Teslim et
                                </button>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </>
  );
}

/** KARGOYA VERMEYİN: packed, then a line of it cancelled; each parcel stays here until a desk packs it again without it. */
function RepackParcels({ parcels }: { parcels: readonly RepackParcel[] }) {
  return (
    <section className="card dns-box" role="alert">
      <div className="card-h">
        <Icon name="alert" />
        <h2>
          KARGOYA VERMEYİN <small>— ürünü iptal edildi, yeniden paketlenecek</small>
        </h2>
        <span className="sub">{parcels.length} paket</span>
      </div>
      <p className="dns-hint">
        Kuryeye vermeyin. Paketi paketleme masasına götürün: açın, iptal edilen ürünü çıkarıp Yerleştirme → Rafa iade ile rafına koyun, kalanını yeniden
        paketleyin; paket yeniden paketlenince listeden düşer.
      </p>
      <div className="dns-list">
        {parcels.map((p) => (
          <div key={p.orderId} className="dns-item">
            <span className="dns-code mono">{p.trackingNumber ?? p.orderNumber}</span>
            <span className="dns-when">{fmt.age(p.since) === '0 dk' ? 'az önce' : `${fmt.age(p.since)} önce`}</span>
            <span className="dns-meta">
              {p.trackingNumber && <span className="mono">{p.orderNumber}</span>}
              <ChannelBadge channel={p.channel} />
              <SellerChip code={p.tenantCode} />
              <span>{p.carrierCode ? carrierLabel(p.carrierCode) : 'Kargo firması belli değil'}</span>
              <b>Çıkarılacak: {cancelledLinesText(p.cancelledLines)}</b>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** KARGOYA VERMEYİN: packed, then cancelled; each parcel stays here until its units are back on the shelf. */
function CancelledParcels({ parcels }: { parcels: readonly CancelledParcel[] }) {
  return (
    <section className="card dns-box" role="alert">
      <div className="card-h">
        <Icon name="alert" />
        <h2>
          KARGOYA VERMEYİN <small>— paketlendikten sonra iptal edildi</small>
        </h2>
        <span className="sub">{parcels.length} paket</span>
      </div>
      <p className="dns-hint">Kuryeye vermeyin. Paketi açın, ürünleri Yerleştirme → Rafa iade ile rafına koyun; ürünler rafa dönünce paket listeden düşer.</p>
      <div className="dns-list">
        {parcels.map((p) => (
          <div key={p.orderId} className="dns-item">
            <span className="dns-code mono">{p.trackingNumber ?? p.orderNumber}</span>
            <span className="dns-when">{fmt.age(p.cancelledAt) === '0 dk' ? 'az önce' : `${fmt.age(p.cancelledAt)} önce`} iptal</span>
            <span className="dns-meta">
              {p.trackingNumber && <span className="mono">{p.orderNumber}</span>}
              <ChannelBadge channel={p.channel} />
              <SellerChip code={p.tenantCode} />
              <span>{p.carrierCode ? carrierLabel(p.carrierCode) : 'Kargo firması belli değil'}</span>
              <span>{p.units} adet</span>
              {p.cancelReason && <span className="muted">{cancelReasonText(p.cancelReason)}</span>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
